import { AssigneeSelection } from '@chilipiper/api-client/src/concierge-fire'
import { RoundRobinExpectedHostV2, User1 } from '@chilipiper/api-client/src/user-availability'
import {
  PublicOwnershipSchedulingLinkV2DistributionHost,
  PublicOwnershipSchedulingLinkV2OwnershipHost,
  PublicRoundRobinSchedulingLinkV2Host,
} from '@chilipiper/api-client/src/team-scheduling'
import { DistributionHost } from '../../../types'
import { BookerInvitationSettings, BookerSettings } from './booker-settings'

const getHostIdFromBookerSettings = (bookerSettings?: BookerSettings) => {
  if (bookerSettings?.type === 'Host') {
    return bookerSettings.id
  }
  return undefined
}

type Host =
  | PublicRoundRobinSchedulingLinkV2Host
  | PublicOwnershipSchedulingLinkV2OwnershipHost
  | PublicOwnershipSchedulingLinkV2DistributionHost

export const getRoundRobinAndOwnershipExpectedHost = ({
  host,
  bookerInvitationSettings,
  firstAssignment,
}: {
  bookerInvitationSettings?: BookerInvitationSettings
  host?: Host
  firstAssignment: { userId: string } | { distributionId: string }
}): RoundRobinExpectedHostV2 => {
  if (host?.type === 'Attendee') {
    return {
      userId: host.userId,
      type: 'User',
    }
  }

  if (host?.type === 'BookerIfExists' && bookerInvitationSettings?.type === 'Invite') {
    return {
      userId: bookerInvitationSettings.id,
      type: 'User',
    }
  }

  if (host?.type === 'AssigneeFromDistribution' || host?.type === 'Assignee') {
    return {
      distributionId: host.distributionId,
      type: 'AssigneeFromDistribution',
    }
  }

  if (
    (host?.type === 'AssigneeFromOwnershipRule' || host?.type === 'AssigneeFromSimpleRule') &&
    'userId' in firstAssignment
  ) {
    return {
      userId: firstAssignment.userId,
      type: 'User',
    }
  }

  if ('userId' in firstAssignment) {
    return {
      userId: firstAssignment.userId,
      type: 'User',
    }
  }

  return {
    distributionId: firstAssignment.distributionId,
    type: 'AssigneeFromDistribution',
  }
}

export const getUserExpectedHost = (hostId: string): User1 => {
  return {
    userId: hostId,
    type: 'User',
  }
}

export const convertExpectedHostToDistributionHost = (
  expectedHost: RoundRobinExpectedHostV2
): DistributionHost => {
  switch (expectedHost.type) {
    case 'User':
      return {
        type: 'User',
        id: expectedHost.userId,
      }
    case 'AssigneeFromDistribution':
      return {
        type: 'Assignee',
        distributionId: expectedHost.distributionId,
      }
  }
}

/*
 * concierge helper
 */
export const getRoundRobinExpectedHostFromMultipleAssignments = ({
  bookerSettings,
  selectedAssignee,
}: {
  selectedAssignee: AssigneeSelection
  bookerSettings?: BookerSettings
}): RoundRobinExpectedHostV2 => {
  const hostId = getHostIdFromBookerSettings(bookerSettings)

  if (hostId) {
    return {
      userId: hostId,
      type: 'User',
    } satisfies RoundRobinExpectedHostV2
  }

  if (selectedAssignee.type === 'DistributedV3') {
    return {
      type: 'AssigneeFromDistribution',
      distributionId: selectedAssignee.distributionId,
    } satisfies RoundRobinExpectedHostV2
  }

  return {
    type: 'User',
    userId: selectedAssignee.id,
  } satisfies RoundRobinExpectedHostV2
}
